import {assert} from 'Asserts';
import {InaccessibleInfo} from 'auth/RequestAccess';
import {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  StructuredTableRowUpdaterModel,
  PrimarySample,
  SampleLookupInfo,
  StructuredTableRowUpdater,
} from 'notebook/structured-tables/StructuredTableConstants';

interface Update<TRowMetadata> {
  updatedModel: StructuredTableRowUpdaterModel | InaccessibleInfo;
  rowUpdater: StructuredTableRowUpdater<StructuredTableRowUpdaterModel, TRowMetadata>;
}

type UpdatesToApply<TRowMetadata> = Array<{
  sampleLookupInfo: SampleLookupInfo;
  updates: Array<Update<TRowMetadata>>;
  columnGroup: ColumnGroup | null;
}>;

type SampleWithLookupInfo = PrimarySample & {sampleLookupInfo: SampleLookupInfo};

async function _bulkFetchAllSamples<TRowMetadata>(
  filteredSamples: Array<SampleWithLookupInfo>,
  rowUpdaters: Array<StructuredTableRowUpdater<StructuredTableRowUpdaterModel, TRowMetadata>>
): Promise<UpdatesToApply<TRowMetadata>> {
  // Pre-build necessary data structures to re-map samples and updates for final result
  const queryPromises: Array<Promise<void>> = [];
  const modelIdToUpdates: Record<string, Array<Update<TRowMetadata>>> = {};
  const filteredSampleMap = filteredSamples.reduce<{[id: string]: SampleWithLookupInfo}>((acc, sample) => {
    acc[sample.sampleLookupInfo.idForLookup] = sample;
    return acc;
  }, {});

  // Helper function to map updates to their corresponding sample ID
  const addRowUpdater = (
    rowUpdater: StructuredTableRowUpdater<StructuredTableRowUpdaterModel, TRowMetadata>,
    updatedModel: StructuredTableRowUpdaterModel | InaccessibleInfo
  ): void => {
    const id = updatedModel.id ?? (updatedModel as InaccessibleInfo).invalidId;
    assert(id != null, 'Row updater model must have an id or invalidId');
    modelIdToUpdates[id] ??= [];
    modelIdToUpdates[id].push({rowUpdater, updatedModel});
  };

  // Compile bulk lookup info list
  for (const rowUpdater of rowUpdaters) {
    if (rowUpdater.bulkFetchModel) {
      // If the row updater has bulkFetchModel available, use it to resolve all samples in one promise
      const sampleLookupInfo = filteredSamples.map((sample) => sample.sampleLookupInfo);
      const newPromise = rowUpdater.bulkFetchModel(sampleLookupInfo).then((models): void => {
        // Map the returned models to their corresponding sample ID in the proper update shape
        for (const updatedModel of models) {
          addRowUpdater(rowUpdater, updatedModel);
        }
      });
      queryPromises.push(newPromise);
    } else {
      // Otherwise, push all of the individual promises onto the stack. They will map their model upon returning
      const newPromises = filteredSamples.map(async (sample): Promise<void> => {
        addRowUpdater(rowUpdater, await rowUpdater.fetchModel(sample.sampleLookupInfo));
      });
      queryPromises.push(...newPromises);
    }
  }

  // Resolve all of GraphQL queries, both for individual and bulk
  await Promise.all(queryPromises);

  // Map the updates, which are now hydrated with the above promise resolutions, back to their samples to build the output
  return Object.entries(modelIdToUpdates)
    .filter(([id]) => filteredSampleMap[id] != null)
    .map(([id, updates]) => ({
      sampleLookupInfo: filteredSampleMap[id].sampleLookupInfo,
      updates,
      columnGroup: filteredSampleMap[id].columnGroup,
    }));
}

/**
 * Retrieves all of the samples given using the given rowUpdaters.  The fetches are issued in parallel
 * with the assumption that some other system is batching calls.
 *
 * @param samples - an array of PrimarySamples that we want to fetch
 * @param rowUpdaters - the StructuredTableRowUpdaters that we want to fetch
 */
// Agent keep in sync: bulk_fetch_models_for_kind in benchling/notebook/structured_tables/resync/model_fetcher.py
export default function fetchAllSamples<TRowMetadata>(
  samples: Array<PrimarySample>,
  rowUpdaters: Array<StructuredTableRowUpdater<StructuredTableRowUpdaterModel, TRowMetadata>>
): Promise<UpdatesToApply<TRowMetadata>> {
  // Return early to avoid running Apollo queries if there are no samples to fetch
  if (samples.length === 0 || rowUpdaters.length === 0) {
    return Promise.resolve([]);
  }

  const filteredSamples = samples.filter(
    (sample): sample is SampleWithLookupInfo => sample.sampleLookupInfo != null
  );

  return _bulkFetchAllSamples(filteredSamples, rowUpdaters);
}
